import { Component, OnDestroy, OnInit } from '@angular/core';
import { Constants } from './core/constants/constants';
import { Subscription } from 'rxjs';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { StorageProvider } from './core/http/storage-service';
import { environment } from 'src/environments/environment';
import { NgxPubSubService } from '@pscoped/ngx-pub-sub';
import * as jwt_decode from 'jwt-decode';
import { CommonUtilsService } from './core/services/utils/common-utils.service';
import { DialogService } from '@progress/kendo-angular-dialog';
import { UnAuthorizedAccessModalComponent } from './shared/components/unauthorized-access-modal/unauthorized-access-modal.component';
import { Messages } from './core/messages/messages';
import { LoggingService } from './core/services/utils/logging.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'PIP-fe';
  private subscriptionLoginSuccess: Subscription;
  private subscriptionLoginFailure: Subscription;
  private subscriptionAcquireTokenSuccess: Subscription;
  private subscriptionAcquireTokenFailure: Subscription;

  isIframe: any = false;
  loggedIn: any = false;
  isUserPresent: any = true;
  isLoading = false;
  authenticated: any = false;
  constructor(private broadcastService: BroadcastService, private msalService: MsalService,
              private storageProvider: StorageProvider, private dialogService: DialogService, private loggingService: LoggingService,
              private eventService: NgxPubSubService, private objCommonUtilsProvider: CommonUtilsService) {
  }

  /**
   * Function called on initializing the page
   */
  ngOnInit() {
    const browserName = this.objCommonUtilsProvider.getBrowserName();
    if (browserName !== 'chrome') {
      this.showAlertMessageDialog(Messages.Invalid_Browser);
    } else {
      this.initMSAL();
      this.isUserPresent = this.storageProvider.getSessionStorageData(Constants.IsUserLoggedIn);
      if (this.loggedIn) {
        this.getAccessToken();
      } else {
        this.signIn();
        this.storageProvider.setSessionStorageData(Constants.IsUserLoggedIn, false);
      }
    }
  }

   // Prompt the user to sign in and
  // grant consent to the requested permission scopes
  async signIn(): Promise<void> {
    this.msalService.loginRedirect();
  }

  // Silently request an access token
  async getAccessToken(): Promise<any> {
    const accessTokenRequest = {
      scopes: environment.config.scopes
    };
    this.msalService.acquireTokenSilent(accessTokenRequest);
  }

  /**
   * Function used to check azure account detail
   */
  checkoutAccount() {
    this.loggedIn = !!this.msalService.getAccount();
  }

  /**
   * Function will subscribe MSAL callback events
   */
  initMSAL() {
    this.checkoutAccount();

    this.subscriptionLoginFailure = this.broadcastService.subscribe('msal:loginFailure', (payload) => {
      this.authenticated = false;
      this.loggingService.logException(payload);
      this.showAlertMessageDialog(Messages.Login_Failure_Message).then(data => {
        if (data) {
          this.msalService.logout();
        }
      });
      sessionStorage.setItem('authenticated', 'false');
    });

    this.subscriptionLoginSuccess = this.broadcastService.subscribe('msal:loginSuccess', (payload) => {
      // this.getAccessToken();
      this.checkoutAccount();
    });

    this.msalService.handleRedirectCallback((authError, response) => {
      if (authError) {
        return;
      }
    });

    this.subscriptionAcquireTokenSuccess = this.broadcastService.subscribe('msal:acquireTokenSuccess', (payload) => {
      if (!this.isUserPresent) {
        const expirationDateTime: any = this.getTokenExpirationDate(payload.accessToken);
        const todayDate: any = new Date();
        const timeToRefresh: any = (expirationDateTime - todayDate);
        this.refreshToken(timeToRefresh);
      }
      this.isUserPresent = this.storageProvider.getSessionStorageData(Constants.IsUserLoggedIn);
      sessionStorage.setItem(Constants.AccessToken, JSON.stringify(payload));
      this.eventService.publishEvent(Constants.MsalLoginSuccessEvent, this.isUserPresent);
    });

    this.subscriptionAcquireTokenFailure = this.broadcastService.subscribe('msal:acquireTokenFailure', (payload) => {
      this.loggingService.logException(payload);
      this.showAlertMessageDialog(Messages.Auth_Token_Failure_Message).then(data => {
        if (data) {
          this.msalService.logout();
        }
      });
      this.authenticated = false;
      sessionStorage.setItem('authenticated', 'false');
    });
  }

  /**
   * Function called to login into microsoft site
   */
  login() {
    const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
    if (isIE) {
      this.msalService.loginRedirect();
    } else {
      this.msalService.loginPopup();
    }
  }

  /**
   * Function will parse JWT token to get expiry date
   * @param token JWT token
   */
  getTokenExpirationDate(token: string): Date {
    const decoded = jwt_decode(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  /**
   * Function called to refresh the Azure token
   * @param time Expiry time
   */
  refreshToken(timeToRefresh) {
    timeToRefresh = timeToRefresh - Constants.timeBeforeTokenRefresh;  // Subtracting 2 sec from token expiry time
    const setIntervalId = setInterval(() => {
      this.getAccessToken();
    }, timeToRefresh);    // Already in milliseconds
  }


  /**
   * Function called on page destroy event
   */
  ngOnDestroy() {
    this.broadcastService.getMSALSubject().next(1);
    if (this.subscriptionLoginSuccess) {
      this.subscriptionLoginSuccess.unsubscribe();
    }
    if (this.subscriptionLoginFailure) {
      this.subscriptionLoginFailure.unsubscribe();
    }
    if (this.subscriptionAcquireTokenSuccess) {
      this.subscriptionAcquireTokenSuccess.unsubscribe();
    }
    if (this.subscriptionAcquireTokenFailure) {
      this.subscriptionAcquireTokenFailure.unsubscribe();
    }
  }

  /**
   * Desc: Function to show alert dialog.
   */
  showAlertMessageDialog(msg: string) {
    return new Promise((resolve) => {
      const dialogRef = this.dialogService.open({
        content: UnAuthorizedAccessModalComponent,
      });
      const userInfo = dialogRef.content.instance;
      userInfo.message = msg;
      dialogRef.result
        .subscribe(resp => {
          resolve(true);
        });
    });
  }

}
